import React, { Fragment, useEffect, useState } from 'react';

import Lottie from 'lottie-light-react';
import { BookApi } from '../../API/BookApi';
import Button from '../../Components/Button';
import { useNavigate } from '../../ProtectedRoute';

import animationData from '../../animations/article_builder_inprogress.json';
import { useAuth } from '../../Auth/AuthProvider';
import { AnalyticsEvent, useAnalytics } from '../../Context/AnalyticsProvider';

const PROGRESS_BAR_MS = 75 * 1000;

interface BookBuilderInProgressViewProps {
  requestId: string;
  bookId: string;
  onGenerationError: () => void;
}

const BookBuilderInProgressView: React.FC<BookBuilderInProgressViewProps> = ({
  requestId,
  bookId,
  onGenerationError,
}) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { userId } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [checkTriggeredAt, setCheckTriggeredAt] = useState<number>(Date.now());
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('Starting off with your big idea...');

  useEffect(() => {
    trackEvent(AnalyticsEvent.BOOK_BUILDER_GENERATION_STARTED, {
      requestId,
      bookId,
    });
  }, [bookId, requestId]);

  useEffect(() => {
    const checkStatus = () => {
      trackEvent(AnalyticsEvent.BOOK_BUILDER_GENERATION_WAIT, {
        requestId,
        bookId,
      });

      const bookApi = new BookApi();
      bookApi
        .getUserBookGenerationStatus(requestId, bookId)
        .then(async ({ status, book }) => {
          switch (status) {
            case 'completed':
              trackEvent(AnalyticsEvent.BOOK_BUILDER_GENERATION_COMPLETED, {
                requestId,
                bookId,
              });
              const chapter = book.chapters[0];
              navigate(`/book/${bookId}/${chapter.id}`);
              break;
            case 'failed':
              setErrorMessage(
                "It seems we can't build this story, let's try another one",
              );
              trackEvent(AnalyticsEvent.BOOK_BUILDER_GENERATION_FAILED, {
                requestId,
                bookId,
                error: 'Unknown Error',
              });
              break;
            default:
              // If not completed or failed, schedule next check
              setTimeout(() => setCheckTriggeredAt(Date.now()), 5000);
          }
        })
        .catch((error) => {
          console.error('Error fetching article generation status:', error);
          // Even on error, we want to try again in 5 seconds
          setTimeout(() => setCheckTriggeredAt(Date.now()), 5000);
        });
    };

    checkStatus();
  }, [bookId, requestId, checkTriggeredAt]);

  useEffect(() => {
    let start: number | null = null;
    let oldProgress = 0;
    let animationFrameId: number;

    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsedMilliSeconds = timestamp - start;

      // Calculate progress based on elapsed time, up to 95%
      const progressPercent = Math.min(
        (elapsedMilliSeconds / PROGRESS_BAR_MS) * 100,
        95,
      );
      const roundedProgress = Math.round(progressPercent);

      if (roundedProgress % 20 === 0 && roundedProgress !== oldProgress) {
        switch (roundedProgress) {
          case 20:
            setStatus('Crafting the setting where all the fun happens...');
            break;
          case 40:
            setStatus('Bringing your characters to life...');
            break;
          case 60:
            setStatus('Creating exciting visuals to match your tale...');
            break;
          case 80:
            setStatus('Almost there, adding some finishing touches…');
            break;
        }
        oldProgress = roundedProgress;
      }

      setProgress(progressPercent);

      if (progressPercent < 95) {
        animationFrameId = requestAnimationFrame(step);
      }
    };

    animationFrameId = requestAnimationFrame(step);

    // Cleanup function to cancel animation frame if component unmounts
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  return (
    <Fragment>
      <div className="size-full flex flex-col items-center bg-white">
        <div
          className="max-md:mt-10 max-w-96"
          aria-label="Article building in progress"
          data-testid="article-builder-in-progress-view"
        >
          <Lottie animationData={animationData} loop={true} />
        </div>
        {errorMessage ? (
          <div className="text-base leading-5 text-center text-neutral-800 text-opacity-70">
            {errorMessage}
            <br />
            <Button
              variant="primary"
              className="mt-4"
              onClick={() => {
                onGenerationError();
                navigate('/create-my-own-story');
              }}
            >
              Create Another Book
            </Button>
          </div>
        ) : (
          <>
            <p className="text-base leading-5 text-center text-neutral-800 text-opacity-70 mb-6">
              Hang tight... We're building your story!
              <br />
              This should take about a minute.
            </p>
            <p className="text-base leading-5 text-center text-neutral-800 text-opacity-70 mb-3">
              {status}
            </p>
            <div className="w-1/4 bg-gray-300 rounded-full">
              <div
                className="h-4 bg-blue-500 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </>
        )}
      </div>
    </Fragment>
  );
};

export default BookBuilderInProgressView;
